import { CSSProperties, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { DescLines } from './DescLines';
import type {
  DescLine,
  SpellStat,
  Card,
} from '../../services/CardDescriptionService';
import './ItemCard.scss';

/** Content of a detail card — what a focused cell/item displays. */
export interface ItemCardData {
  icon?: string;
  /** Icon frame overlaid bottom-right of the main icon (e.g. spell on a grimoire). */
  overlay?: string;
  name: string;
  subtitle?: string;
  stats?: SpellStat[];
  /** Optional effect breakdown rendered under the subtitle. */
  lines?: DescLine[];
  /** Root card driving the related-cards closure; used by CardDetail, not rendered here. */
  rootCard?: Card;
}

interface Props extends ItemCardData {
  // When provided, the card is a viewport-fixed tooltip at these coords
  // (centered via `translateX(-50%)`) portaled to <body> — so it escapes any
  // clipping/overflow AND `transform`ed ancestor (e.g. the scaled loadout
  // root, which would otherwise capture `position: fixed`). Without `pos` it
  // follows normal flow.
  pos?: { top: number; left: number };
  className?: string;
}

export const ItemCard = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { icon, overlay, name, subtitle, stats, lines, pos, className } = props;
  const style: CSSProperties | undefined = pos
    ? { position: 'fixed', top: pos.top, left: pos.left }
    : undefined;
  const card = (
    <div
      ref={ref}
      className={`item-card${pos ? ' item-card--floating' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      <Icon
        sprite={icon}
        size={64}
        overlay={overlay}
        className="item-card-icon"
      />
      <div className="item-card-name">{name}</div>
      {subtitle && <div className="item-card-subtitle">{subtitle}</div>}
      {stats && stats.length > 0 && (
        <div className="item-card-stats">
          {stats.map((s) => (
            <div className="item-card-stat" key={s.label}>
              <span className="item-card-stat-label">{s.label}</span>
              <span
                className="item-card-stat-value"
                style={s.color ? { color: s.color } : undefined}
              >
                {s.value}
              </span>
            </div>
          ))}
        </div>
      )}
      {lines && lines.length > 0 && <DescLines lines={lines} />}
    </div>
  );
  return pos ? createPortal(card, document.body) : card;
});

ItemCard.displayName = 'ItemCard';
